import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import ImageUploader from '../../components/ImageUploader';
import {
  Button, Chip, Empty, Input, Loading,
} from '../../components/Ui';
import { useToast } from '../../context/ToastContext';
import { dayMonth } from '../../utils/format';
import { CAR_BRANDS, VEHICLE_BRANDS, VEHICLE_ICON } from '../../constants';

const BLANK = {
  kind: 'bike', brand: '', model: '', regNumber: '', year: '', colour: '',
  fuelType: 'petrol', currentKm: '', rcNumber: '', rcFile: '', photo: '',
};

/** The customer's own two-wheelers, with their service history. */
const MyBikesPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [vehicles, setVehicles] = useState([]);
  const [draft, setDraft] = useState(null);
  // Opened from the car module or the bike module: the form starts on that kind; ?add=1 opens it straight away.
  const [params] = useSearchParams();
  const startKind = params.get('family') === 'car' ? 'car' : 'bike';
  useEffect(() => { if (params.get('add')) setDraft({ ...BLANK, kind: startKind }); }, [params, startKind]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.garage.vehicles()
      .then((r) => setVehicles(r.data || []))
      .catch(() => setVehicles([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    if (!draft.brand && !draft.model) {
      toast.error('Tell us the make and model.', 'Add the basics');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        kind: draft.kind,
        brand: draft.brand,
        model: draft.model,
        regNumber: draft.regNumber,
        year: Number(draft.year) || undefined,
        colour: draft.colour,
        fuelType: draft.fuelType,
        currentKm: Number(draft.currentKm) || 0,
        rcNumber: draft.rcNumber,
        rcFile: draft.rcFile,
        photo: draft.photo,
      };
      if (draft._id) await api.garage.updateVehicle(draft._id, payload);
      else await api.garage.addVehicle(payload);
      setDraft(null);
      load();
    } catch (err) {
      toast.error(err.message, 'Could not save');
    } finally { setSaving(false); }
  };

  const remove = async (vehicle) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${vehicle.brand} ${vehicle.model}?`)) return;
    await api.garage.removeVehicle(vehicle._id);
    load();
  };

  if (loading) return <Loading text="Loading your vehicles…" />;

  return (
    <div style={{ maxWidth: 820 }}>
      <PageHeader title="My vehicles" subtitle={`${vehicles.length} vehicle${vehicles.length === 1 ? '' : 's'} - bikes, scooters & cars`} back />

      {!vehicles.length && !draft ? (
        <Empty emoji="🏍️" title="No vehicle added" text="Add your bike or scooter once, then book a service in two clicks." />
      ) : null}

      {vehicles.map((v) => (
        <div className="card sh-sm" key={v._id} style={{ marginBottom: 12 }}>
          <div className="row" style={{ gap: 14 }}>
            {v.photo ? (
              <img src={fileUrl(v.photo)} alt={v.model} style={{ width: 76, height: 76, borderRadius: 12, objectFit: 'cover' }} />
            ) : <span style={{ fontSize: 30 }}>{VEHICLE_ICON[v.kind] || '🏍️'}</span>}
            <div className="grow">
              <div className="t-title">{v.brand} {v.model}</div>
              <div className="t-tiny">{[v.regNumber, v.year, v.colour].filter(Boolean).join(' · ')}</div>
              <div className="t-tiny">{v.currentKm} km · {v.fuelType}</div>
              {v.nextServiceAt ? (
                <div className="t-tiny" style={{ color: 'var(--primary)', fontWeight: 700 }}>
                  Next service {dayMonth(v.nextServiceAt)} or {v.nextServiceKm} km
                </div>
              ) : null}
            </div>
          </div>
          <div className="row" style={{ gap: 16, marginTop: 10 }}>
            <button type="button" className="link-btn" onClick={() => navigate(`/my-vehicles-service/${v._id}/history`)}>📚 Service history</button>
            <button type="button" className="link-btn" onClick={() => setDraft({ ...v, year: v.year ? String(v.year) : '', currentKm: String(v.currentKm || '') })}>Edit</button>
            <button type="button" className="link-btn" style={{ color: 'var(--danger)' }} onClick={() => remove(v)}>Remove</button>
          </div>
        </div>
      ))}

      {draft ? (
        <div className="card sh-sm">
          <div className="t-title">{draft._id ? 'Edit vehicle' : 'Add a vehicle'}</div>
          <div className="row wrap" style={{ gap: 8, margin: '10px 0' }}>
            <Chip label="🏍️ Bike" active={draft.kind === 'bike'} onClick={() => setDraft({ ...draft, kind: 'bike' })} />
            <Chip label="🛵 Scooter" active={draft.kind === 'scooter'} onClick={() => setDraft({ ...draft, kind: 'scooter' })} />
            <Chip label="🚗 Car" active={draft.kind === 'car'} onClick={() => setDraft({ ...draft, kind: 'car', brand: '' })} />
          </div>

          <label className="field-label">Company</label>
          <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
            {(draft.kind === 'car' ? CAR_BRANDS : VEHICLE_BRANDS).map((b) => (
              <Chip key={b} label={b} active={draft.brand === b} onClick={() => setDraft({ ...draft, brand: b })} />
            ))}
          </div>

          <Input label="Model" placeholder="Splendor Plus, Activa 6G…" value={draft.model} onChange={(e) => setDraft({ ...draft, model: e.target.value })} />
          <div className="split-2" style={{ gap: 12 }}>
            <Input label="Registration number" placeholder="HR02AB1234" value={draft.regNumber} onChange={(e) => setDraft({ ...draft, regNumber: e.target.value })} />
            <Input label="Year" inputMode="numeric" value={draft.year} onChange={(e) => setDraft({ ...draft, year: e.target.value.replace(/\D/g, '') })} />
          </div>
          <div className="split-2" style={{ gap: 12 }}>
            <Input label="Colour" value={draft.colour} onChange={(e) => setDraft({ ...draft, colour: e.target.value })} />
            <Input label="Current km" inputMode="numeric" value={draft.currentKm} onChange={(e) => setDraft({ ...draft, currentKm: e.target.value.replace(/\D/g, '') })} />
          </div>

          <label className="field-label">Fuel</label>
          <div className="row wrap" style={{ gap: 8, marginBottom: 12 }}>
            {(draft.kind === 'car' ? ['petrol', 'diesel', 'cng', 'electric', 'hybrid'] : ['petrol', 'electric', 'hybrid']).map((f) => (
              <Chip key={f} label={f} active={draft.fuelType === f} onClick={() => setDraft({ ...draft, fuelType: f })} />
            ))}
          </div>

          <Input label="RC number (optional)" value={draft.rcNumber} onChange={(e) => setDraft({ ...draft, rcNumber: e.target.value })} />
          <ImageUploader label="RC photo (optional)" folder="document" multiple={false} value={draft.rcFile} onChange={(v) => setDraft({ ...draft, rcFile: v })} />
          <ImageUploader label="Vehicle photo" folder="vehicle" multiple={false} value={draft.photo} onChange={(v) => setDraft({ ...draft, photo: v })} />

          <div className="row" style={{ gap: 12 }}>
            <Button title="Save" loading={saving} onClick={save} />
            <Button title="Cancel" variant="light" onClick={() => setDraft(null)} />
          </div>
        </div>
      ) : (
        <Button title="+ Add a vehicle" onClick={() => setDraft({ ...BLANK, kind: startKind })} />
      )}
    </div>
  );
};

export default MyBikesPage;
